import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { ID } from '@datassential/platform-ui-lib';
import { WorkspaceEnum } from '@models/Workspace';

interface HomeWorkspaceContextData {
    workspaceId: WorkspaceEnum;
    chainId: ID | null;
    chainName: string;
    visibleSections: Set<string>;
    registerSection: (id: string) => void;
    unregisterSection: (id: string) => void;
    isReady: boolean;
    markReady: () => void;
}

const defaultContext: HomeWorkspaceContextData = {
    workspaceId: WorkspaceEnum.Manufacturers,
    chainId: null,
    chainName: '',
    visibleSections: new Set(),
    registerSection: () => {},
    unregisterSection: () => {},
    isReady: false,
    markReady: () => {},
};

const HomeWorkspaceContext = createContext<HomeWorkspaceContextData>(defaultContext);

export const useHomeWorkspace = () => useContext(HomeWorkspaceContext);

interface HomeWorkspaceProviderProps {
    workspaceId: WorkspaceEnum;
    chainId: ID | null;
    chainName: string;
    defaultVisibleSections?: string[];
    children: ReactNode;
}

export const HomeWorkspaceProvider: FC<HomeWorkspaceProviderProps> = ({
    workspaceId,
    chainId,
    chainName,
    defaultVisibleSections = [],
    children,
}) => {
    const [visibleSections, setVisibleSections] = useState<Set<string>>(
        () => new Set(defaultVisibleSections)
    );
    const [isReady, setIsReady] = useState(false);

    const markReady = useCallback(() => setIsReady(true), []);

    const registerSection = useCallback((id: string) => {
        setVisibleSections(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const unregisterSection = useCallback((id: string) => {
        setVisibleSections(prev => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const value = useMemo((): HomeWorkspaceContextData => ({
        workspaceId,
        chainId,
        chainName,
        visibleSections,
        registerSection,
        unregisterSection,
        isReady,
        markReady,
    }), [workspaceId, chainId, chainName, visibleSections, registerSection, unregisterSection, isReady, markReady]);

    return (
        <HomeWorkspaceContext.Provider value={value}>
            {children}
        </HomeWorkspaceContext.Provider>
    );
};

export default HomeWorkspaceContext;
